// import { LoadingOutlined } from "@ant-design/icons";
import { CloseCircleFilled } from "@ant-design/icons";
import { Image, Modal, Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import VideoIcon from "../../assets/icons/jouer.svg";
import LockIcon from "../../assets/icons/lock-outlined-without-point.svg";
import { FALLBACK_IMAGE } from "../../helpers/constant";
import { authService } from "../../services";
import { addWatermark } from "../../utls/FunctionsUtil";
import VideoPlayer from "../VideoPlayerComponent";
import AudioMiniPlayer from "../AudioMiniPlayer/AudioMiniPlayer";
import AudioPlayerComponent from "../AudioPlayerComponent/AudioPlayerComponent";
import "./index.less";

type props = {
  image: string;
  width: number;
  height: number;
  borderRadius: number;
  isPrivate?: boolean;
  isBlur?: boolean;
  isVideo?: boolean;
  isAudio?: boolean;
  url?: string;
  username?: string;
  shouldAddWatermark: boolean;
  displayVideo?: boolean;
  fromCollection?: boolean;
};

const MediaComponent: React.FC<props> = ({
  image,
  width,
  height,
  borderRadius,
  isPrivate,
  isVideo,
  isAudio,
  url,
  username,
  isBlur = true,
  shouldAddWatermark = true,
  displayVideo = false,
  fromCollection = false,
}) => {
  const [watermarkedImageUrl, setWatermarkedImageUrl] = useState<string>(shouldAddWatermark && !isPrivate ? "" : image);
  const [watermarkedOriginalUrl, setWatermarkedOriginalUrl] = useState<string>(shouldAddWatermark && !isPrivate ? "" : url!);
  const [showAudioModal, setShowAudioModal] = useState<boolean>(false);
  const [isLandscape, setIsLandscape] = useState<boolean>(false);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const audioMetadataRef = React.useRef<HTMLAudioElement>(null);

  // Detect device orientation for responsive layout
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    // Check on mount
    checkOrientation();

    // Add event listener for orientation changes
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  useEffect(() => {
    if (isAudio && url && audioMetadataRef.current) {
      const audioElement = audioMetadataRef.current;
      const handleLoadedMetadata = () => {
        const duration = audioElement.duration;
        setAudioDuration(duration);
      };

      audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      return () => {
        audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [isAudio, url]);

  useEffect(() => {
    //const proxyUrl = "https://cors-anywhere.herokuapp.com/";
    //const imgUrl = proxyUrl + image;
    if (shouldAddWatermark && !isPrivate) {
      addWatermark(image, authService.getUser().username)
        .then((newImageUrl) => {
          setWatermarkedImageUrl(newImageUrl);
        })
        .catch((err) => {
          console.log("Error generating watermarked image:", err);
          setWatermarkedImageUrl(image);
        });
      if (!isVideo) {
        addWatermark(url!, authService.getUser().username)
          .then((newImageUrl) => {
            setWatermarkedOriginalUrl(newImageUrl);
          })
          .catch((err) => {
            console.log("Error generating watermarked image:", err);
            setWatermarkedOriginalUrl(url!);
          });
      }
    }
  }, [image, shouldAddWatermark, isPrivate]);

  // Audio modal handlers
  const handleOpenAudioModal = () => {
    if (!isPrivate || displayVideo) {
      setShowAudioModal(true);
    }
  };

  const handleCloseAudioModal = () => {
    setShowAudioModal(false);
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (watermarkedImageUrl === "" && !isPrivate && !isAudio) {
    return (
      <div className={"skeleton-wrapper d-flex justify-content-center items-center"} style={{ width: width, height: height }}>
        {/* <LoadingOutlined style={{ color: "white", fontSize: "50px" }} /> */}
        <Skeleton.Node active={true} style={{ width: width, height: height }} />
      </div>
    );
  }

  return (
    <div className="mediaWrapper" style={{ width: width, height: height }}>
      {isAudio && url && <audio ref={audioMetadataRef} src={url} preload="metadata" style={{ display: "none" }} />}

      <div
        className="mediaContent"
        style={{
          width: width,
          height: height,
          borderRadius: borderRadius,
          filter: isPrivate && isBlur ? "blur(5px)" : "",
          backgroundColor: isAudio ? "#000000" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isAudio ? (
          <>
            <div style={{ width: "100%", height: "100%" }}>
              <AudioMiniPlayer
                width={width}
                height={height}
                borderRadius={borderRadius}
                onOpenModal={handleOpenAudioModal}
                isPlayable={!isPrivate || displayVideo} // Pass isPlayable prop
              />
              <div className="duration-overlay">{formatTime(audioDuration)}</div>
            </div>
            <Modal
              open={showAudioModal}
              onCancel={handleCloseAudioModal}
              footer={null}
              width={isLandscape ? "80%" : "90%"}
              centered
              destroyOnClose
              className={`audio-preview-modal ${isLandscape ? "landscape" : "portrait"}`}
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.85)",
              }}
              closeIcon={<CloseCircleFilled style={{ color: "white", fontSize: "24px" }} />}
            >
              <div className="audio-preview-wrapper">
                <AudioPlayerComponent audioUrl={url!} autoPlay={true} />
              </div>
            </Modal>
          </>
        ) : isVideo ? (
          <Image
            loading="lazy"
            width={width}
            height={height}
            src={watermarkedImageUrl}
            fallback={FALLBACK_IMAGE}
            style={{ borderRadius: borderRadius, objectFit: "cover" }}
            preview={
              fromCollection && isPrivate
                ? false
                : isPrivate && !displayVideo
                  ? {}
                  : {
                      destroyOnClose: true,
                      modalRender: () => (
                        <div className="video-preview-wrapper">
                          <VideoPlayer videoUrl={url!} thumbnailUrl={""} autoPlay={true} />
                        </div>
                      ),
                    }
            }
          />
        ) : (
          <Image
            loading="lazy"
            width={width}
            height={height}
            src={watermarkedImageUrl}
            fallback={FALLBACK_IMAGE}
            style={{ borderRadius: borderRadius, objectFit: "cover" }}
            preview={
              fromCollection && isPrivate
                ? false
                : {
                    src: watermarkedOriginalUrl,
                  }
            }
            // preview={fromCollection && isPrivate ? false : true}
          />
        )}
      </div>
      {isPrivate && !fromCollection && <img className={`${isAudio ? "locksIconAudio" : "locksIcon"}`} src={LockIcon} width={15} alt="lock" />}
      {isVideo && <img className={`videosIcon ${fromCollection ? "collection" : ""}`} src={VideoIcon} alt="play" />}
    </div>
  );
};

function areEqual(prev: Readonly<props>, next: Readonly<props>) {
  return (
    prev.image === next.image &&
    prev.width === next.width &&
    prev.height === next.height &&
    prev.borderRadius === next.borderRadius &&
    prev.isPrivate === next.isPrivate &&
    prev.isBlur === next.isBlur &&
    prev.isVideo === next.isVideo &&
    prev.url === next.url &&
    prev.username === next.username &&
    prev.shouldAddWatermark === next.shouldAddWatermark &&
    prev.displayVideo === next.displayVideo &&
    prev.fromCollection === next.fromCollection &&
    prev.isAudio === next.isAudio
  );
}

export default React.memo(MediaComponent, areEqual);
